import type { AvatarBase, UserCosmeticLoadout, UserProfile } from "../../types";
import {
  MAP_AVATAR_BASE_ASSETS,
  MAP_AVATAR_FALLBACKS,
  MAP_AVATAR_ITEM_ASSETS,
  type MapAvatarAssetDescriptor,
  type MapAvatarAssetSlot,
  type MapAvatarLayerKey,
} from "../constants/mapAvatarAssets";

const AVATAR_BASES: readonly AvatarBase[] = [
  "user_icon",
  "boy",
  "girl",
  "dad",
  "mom",
  "grandpa",
  "grandma",
];

const LEGACY_AVATAR_BASE_ALIASES: Record<string, AvatarBase> = {
  boy: "user_icon",
  girl: "user_icon",
  dad: "user_icon",
  mom: "user_icon",
  grandpa: "user_icon",
  grandma: "user_icon",
};

type BadgeSlot = "badgeA" | "badgeB" | "badgeC";
type BadgePosition = "left" | "right" | "bottom";

export type ResolvedMapAvatarLayer = {
  key: string;
  slot: MapAvatarAssetSlot;
  layer: MapAvatarLayerKey;
  asset: string;
  cosmeticId?: string;
  name?: string;
  fallback?: boolean;
};

export type ResolvedMapAvatarBadge = {
  key: BadgeSlot;
  asset: string;
  cosmeticId?: string;
  name?: string;
  position: BadgePosition;
};

export type ResolvedMapAvatar = {
  base: AvatarBase;
  baseAsset: string;
  /** Deprecated V4.5: kept only so older callers do not break. */
  composedAsset?: string;
  /** Deprecated V4.5: kept only so older callers do not break. */
  composedKey?: string;
  layers: ResolvedMapAvatarLayer[];
  badges: ResolvedMapAvatarBadge[];
  profileFrameTone?: string;
  profileFrameAccentAsset?: string;
  equipped: Partial<Record<MapAvatarAssetSlot, MapAvatarAssetDescriptor>>;

  // Compatibility fields for any older caller while Map Avatar V4 settles.
  hatAsset?: string;
  topAsset?: string;
  rodAsset?: string;
  charmAsset?: string;
  hatCosmeticId?: string;
  topCosmeticId?: string;
  rodCosmeticId?: string;
  charmCosmeticId?: string;
};

const normalizeAvatarBase = (value: unknown): AvatarBase => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw in LEGACY_AVATAR_BASE_ALIASES) return LEGACY_AVATAR_BASE_ALIASES[raw];
  return (AVATAR_BASES as readonly string[]).includes(raw)
    ? (raw as AvatarBase)
    : MAP_AVATAR_FALLBACKS.base;
};

const COSMETIC_ID_ALIASES: Record<string, string> = {
  // hats
  basic_hat: "rookie_cap",
  starter_hat: "rookie_cap",
  rookie_hat: "rookie_cap",
  rookie_cap_v2: "rookie_cap",
  map_rookie_cap: "rookie_cap",
  avatar_rookie_cap: "rookie_cap",
  hat_rookie_cap: "rookie_cap",

  // tops / outfit
  basic_jacket: "bay_hoodie",
  basic_hoodie: "bay_hoodie",
  baleen_bay_hoodie: "bay_hoodie",
  bay_hoodie_v2: "bay_hoodie",
  hoodie_bay: "bay_hoodie",
  starter_top: "bay_hoodie",
  starter_outfit: "bay_hoodie",
  avatar_bay_hoodie: "bay_hoodie",

  // rods
  basic_rod: "starter_rod_skin",
  starter_rod: "starter_rod_skin",
  starter_rod_skin_v2: "starter_rod_skin",
  starter_rodskin: "starter_rod_skin",
  starter_rod_skin_map: "starter_rod_skin",
  avatar_starter_rod_skin: "starter_rod_skin",

  // charms
  basic_lure: "lucky_minnow_charm",
  minnow_charm: "lucky_minnow_charm",
  lucky_minnow: "lucky_minnow_charm",
  lucky_minnow_charm_v2: "lucky_minnow_charm",
  starter_charm: "lucky_minnow_charm",
  lure_lucky_minnow_charm: "lucky_minnow_charm",
  avatar_lucky_minnow_charm: "lucky_minnow_charm",

  // frames
  bronze_border: "harbor_bronze_frame",
  bronze_boarder: "harbor_bronze_frame",
  bronze_frame: "harbor_bronze_frame",
  harbor_bronze: "harbor_bronze_frame",
  harbor_bronze_frame_v2: "harbor_bronze_frame",
  starter_frame: "harbor_bronze_frame",
  gold_frame: "golden_bass_frame",
  golden_frame: "golden_bass_frame",
};

const objectIdKeys = [
  "cosmeticId",
  "id",
  "itemId",
  "assetId",
  "sku",
  "slug",
  "key",
  "name",
] as const;

const normalizeLoadoutId = (value: unknown): string => {
  if (!value) return "";

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of objectIdKeys) {
      const raw = record[key];
      if (raw) return normalizeLoadoutId(raw);
    }
    return "";
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\.png$|\.svg$|\.jpg$|\.jpeg$|\.webp$/i, "")
    .split("/")
    .pop()!
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

const resolveCosmeticId = (value: unknown) => {
  const normalized = normalizeLoadoutId(value);
  if (!normalized) return undefined;
  return COSMETIC_ID_ALIASES[normalized] || normalized;
};

const isMapAvatarDebugEnabled = () =>
  typeof globalThis !== "undefined" && Boolean((globalThis as any).__RODDO_DEBUG_MAP_AVATAR);

const layerForSlot = (slot: MapAvatarAssetSlot): MapAvatarLayerKey | undefined => {
  switch (slot) {
    case "hair": return "hairFront";
    case "hat": return "hat";
    case "shirt": return "shirt";
    case "top": return "top";
    case "outerwear": return "outerwear";
    case "pants": return "pants";
    case "socks": return "socks";
    case "footwear": return "footwear";
    case "faceCover": return "faceCover";
    case "rodSkin": return "rodSkin";
    case "reelSkin": return "reelSkin";
    case "lureCharm": return "lureCharm";
    default: return undefined;
  }
};

const firstSlotValue = (loadout: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = loadout[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const makeLayer = ({
  slot,
  cosmeticId,
}: {
  slot: MapAvatarAssetSlot;
  cosmeticId?: unknown;
}): ResolvedMapAvatarLayer | undefined => {
  const resolvedCosmeticId = resolveCosmeticId(cosmeticId);
  if (!resolvedCosmeticId) return undefined;

  const item = MAP_AVATAR_ITEM_ASSETS[resolvedCosmeticId];
  const layer = item?.layer || layerForSlot(slot);

  // V4.12.1 launch rule: no visible fake fallback stickers for empty slots.
  // A slot only renders when the equipped value resolves to a real map asset.
  if (!item?.asset || !layer) return undefined;

  return {
    key: `${slot}:${resolvedCosmeticId}`,
    slot,
    layer,
    asset: item.asset,
    cosmeticId: resolvedCosmeticId,
    name: item.name,
    fallback: false,
  };
};

const makeBadge = (
  key: BadgeSlot,
  cosmeticId: unknown,
  position: BadgePosition,
): ResolvedMapAvatarBadge | undefined => {
  const resolvedCosmeticId = resolveCosmeticId(cosmeticId);
  if (!resolvedCosmeticId) return undefined;
  const item = MAP_AVATAR_ITEM_ASSETS[resolvedCosmeticId];
  const asset = item?.badgeAsset || item?.accent || MAP_AVATAR_FALLBACKS.badge;
  return { key, asset, cosmeticId: resolvedCosmeticId, name: item?.name, position };
};

export const resolveMapAvatar = (profile?: Partial<UserProfile> | null): ResolvedMapAvatar => {
  const anyProfile = (profile || {}) as any;
  const loadout = (anyProfile.cosmeticLoadout || {}) as Record<string, unknown> & UserCosmeticLoadout;
  const base = normalizeAvatarBase(anyProfile.avatarBase);
  const baseAsset = MAP_AVATAR_BASE_ASSETS[base] || MAP_AVATAR_BASE_ASSETS[MAP_AVATAR_FALLBACKS.base];

  const profileFrameValue = firstSlotValue(loadout, ["profileFrame", "frame", "profile_frame", "border"]);
  const profileFrameId = resolveCosmeticId(profileFrameValue);
  const profileFrame = profileFrameId ? MAP_AVATAR_ITEM_ASSETS[profileFrameId] : undefined;

  const hatValue = firstSlotValue(loadout, ["hat", "headwear", "cap"]);
  const topValue = firstSlotValue(loadout, ["top", "outfit", "shirt", "hoodie"]);
  const outerwearValue = firstSlotValue(loadout, ["outerwear", "jacket"]);
  const pantsValue = firstSlotValue(loadout, ["pants"]);
  const footwearValue = firstSlotValue(loadout, ["footwear", "boots", "shoes"]);
  const rodValue = firstSlotValue(loadout, ["rodSkin", "rod", "rodskin", "rod_skin"]);
  const reelValue = firstSlotValue(loadout, ["reelSkin", "reel", "reel_skin"]);
  const charmValue = firstSlotValue(loadout, ["lureCharm", "charm", "lure", "lure_charm"]);
  const backgroundValue = firstSlotValue(loadout, ["background", "profileBackground", "profile_background"]);
  const titleValue = firstSlotValue(loadout, ["title", "profileTitle", "profile_title"]);

  const layers = [
    makeLayer({ slot: "hair", cosmeticId: firstSlotValue(loadout, ["hair"]) }),
    makeLayer({ slot: "socks", cosmeticId: firstSlotValue(loadout, ["socks"]) }),
    makeLayer({ slot: "pants", cosmeticId: pantsValue }),
    makeLayer({ slot: "footwear", cosmeticId: footwearValue }),
    makeLayer({ slot: "top", cosmeticId: topValue }),
    makeLayer({ slot: "outerwear", cosmeticId: outerwearValue }),
    makeLayer({ slot: "faceCover", cosmeticId: firstSlotValue(loadout, ["faceCover", "face_cover"]) }),
    makeLayer({ slot: "rodSkin", cosmeticId: rodValue }),
    makeLayer({ slot: "reelSkin", cosmeticId: reelValue }),
    makeLayer({ slot: "lureCharm", cosmeticId: charmValue }),
    makeLayer({ slot: "hat", cosmeticId: hatValue }),
  ].filter(Boolean) as ResolvedMapAvatarLayer[];

  const badges = [
    makeBadge("badgeA", firstSlotValue(loadout, ["badgeA", "badge_a"]), "left"),
    makeBadge("badgeB", firstSlotValue(loadout, ["badgeB", "badge_b"]), "right"),
    makeBadge("badgeC", firstSlotValue(loadout, ["badgeC", "badge_c"]), "bottom"),
  ].filter(Boolean) as ResolvedMapAvatarBadge[];

  const equipped: Partial<Record<MapAvatarAssetSlot, MapAvatarAssetDescriptor>> = {};
  ([
    ["hat", hatValue],
    ["top", topValue],
    ["outerwear", outerwearValue],
    ["pants", pantsValue],
    ["footwear", footwearValue],
    ["rodSkin", rodValue],
    ["reelSkin", reelValue],
    ["lureCharm", charmValue],
    ["profileFrame", profileFrameValue],
    ["background", backgroundValue],
    ["title", titleValue],
    ["badgeA", firstSlotValue(loadout, ["badgeA", "badge_a"])],
    ["badgeB", firstSlotValue(loadout, ["badgeB", "badge_b"])],
    ["badgeC", firstSlotValue(loadout, ["badgeC", "badge_c"])],
  ] as Array<[MapAvatarAssetSlot, unknown]>).forEach(([slot, value]) => {
    const id = resolveCosmeticId(value);
    if (id && MAP_AVATAR_ITEM_ASSETS[id]) equipped[slot] = MAP_AVATAR_ITEM_ASSETS[id];
  });

  const hatLayer = layers.find((layer) => layer.slot === "hat");
  const topLayer = layers.find((layer) => layer.slot === "outerwear") || layers.find((layer) => layer.slot === "top");
  const rodLayer = layers.find((layer) => layer.slot === "rodSkin");
  const charmLayer = layers.find((layer) => layer.slot === "lureCharm");

  if (isMapAvatarDebugEnabled()) {
    console.debug("[Roddo] resolved map avatar", {
      base,
      loadout,
      normalizedLoadout: {
        hat: resolveCosmeticId(hatValue),
        outfit: resolveCosmeticId(topValue),
        rod: resolveCosmeticId(rodValue),
        charm: resolveCosmeticId(charmValue),
        profileFrame: profileFrameId,
      },
      layers: layers.map((layer) => ({ slot: layer.slot, id: layer.cosmeticId, asset: layer.asset })),
      badges,
    });
  }

  return {
    base,
    baseAsset,
    composedAsset: undefined,
    composedKey: undefined,
    layers,
    badges,
    profileFrameTone: profileFrame?.tone || MAP_AVATAR_FALLBACKS.profileFrameTone,
    profileFrameAccentAsset: profileFrame?.accent,
    equipped,
    hatAsset: hatLayer?.asset,
    topAsset: topLayer?.asset,
    rodAsset: rodLayer?.asset,
    charmAsset: charmLayer?.asset,
    hatCosmeticId: hatLayer?.cosmeticId,
    topCosmeticId: topLayer?.cosmeticId,
    rodCosmeticId: rodLayer?.cosmeticId,
    charmCosmeticId: charmLayer?.cosmeticId,
  };
};
